import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as ArrowDown } from 'src/Images/icons/common/arrowdown.svg';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as GRNList } from 'src/Images/icons/sidebar/GRNList.svg';
import getColorCode from 'src/utils/GetThemeColor';
import 'src/style/createVehicleTracking.css';

const getFirstLineItem = (deliveryObj) => deliveryObj?.line_items?.[0] || {};

const getDeliverySearchProduct = (deliveryObj) => {
  const lineItem = getFirstLineItem(deliveryObj);
  return lineItem?.product?.product_name || '';
};

const updateLoadSequence = (loads) =>
  (loads || []).map((loadObj, index) => {
    const loadNumber = index + 1;
    return {
      ...loadObj,
      load_number: loadNumber,
      title: `Loading ${String(loadNumber).padStart(2, '0')}`,
    };
  });

function LoadTab({
  deliveryLineItem = [],
  loadLineItem = [],
  setLoadLineItem,
  selectedStep,
  setSelectedStep,
  totalSteps,
}) {
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const [expandedLoadId, setExpandedLoadId] = useState('');
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [activeDeliveryListTab, setActiveDeliveryListTab] =
    useState('searched');
  const [selectedDeliveryMap, setSelectedDeliveryMap] = useState({});
  const [searchFormData, setSearchFormData] = useState({
    deliveryNumber: '',
    product: '',
  });
  const [appliedSearchFormData, setAppliedSearchFormData] = useState({
    deliveryNumber: '',
    product: '',
  });

  const listTabActiveStyle = {
    backgroundColor: getColorCode(themeColor),
    borderColor: getColorCode(themeColor),
  };

  const toggleEditDrawer = (open) => () => {
    setOpenEditDrawer(open);
    if (open) {
      setSelectedDeliveryMap({});
      setActiveDeliveryListTab('searched');
      setSearchFormData({
        deliveryNumber: '',
        product: '',
      });
      setAppliedSearchFormData({
        deliveryNumber: '',
        product: '',
      });
    }
  };

  const handleToggleLoad = (loadId) => {
    setExpandedLoadId((currentLoadId) =>
      currentLoadId === loadId ? '' : loadId,
    );
  };

  const handleSearchFormChange = (field, value) => {
    setSearchFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchSubmit = () => {
    setAppliedSearchFormData(searchFormData);
  };

  const deliveryUniqueNumberWithLoadAttached = new Set(
    (loadLineItem || []).flatMap((loadObj) =>
      (loadObj.deliveries || []).map(
        (deliveryObj) => deliveryObj?.delivery_unique_number,
      ),
    ),
  );

  const availableDeliveryList = (deliveryLineItem || []).filter(
    (deliveryObj) =>
      !deliveryUniqueNumberWithLoadAttached.has(
        deliveryObj?.delivery_unique_number,
      ),
  );
  const attachedDeliveryList = Object.values(selectedDeliveryMap);
  const filteredDeliveryList = availableDeliveryList.filter((deliveryObj) => {
    const deliverySearchTerm = appliedSearchFormData.deliveryNumber
      .trim()
      .toLowerCase();
    const productSearchTerm = appliedSearchFormData.product
      .trim()
      .toLowerCase();
    const deliveryNumber = String(deliveryObj?.sap_code || '').toLowerCase();
    const productName = getDeliverySearchProduct(deliveryObj).toLowerCase();
    const matchesDelivery =
      !deliverySearchTerm || deliveryNumber.includes(deliverySearchTerm);
    const matchesProduct =
      !productSearchTerm || productName.includes(productSearchTerm);

    return matchesDelivery && matchesProduct;
  });
  const currentDeliveryList =
    activeDeliveryListTab === 'searched'
      ? filteredDeliveryList
      : attachedDeliveryList;

  const isDeliveryChecked = (deliveryObj) =>
    Boolean(selectedDeliveryMap[deliveryObj?.delivery_unique_number]);

  const handleToggleDeliverySelection = (deliveryObj, checked) => {
    const deliveryKey = deliveryObj?.delivery_unique_number;
    setSelectedDeliveryMap((prev) => {
      const next = { ...prev };
      if (checked) {
        next[deliveryKey] = deliveryObj;
      } else {
        delete next[deliveryKey];
      }
      return next;
    });
  };

  const handleCreateLoadSubmit = () => {
    const selectedDeliveries = Object.values(selectedDeliveryMap);
    if (!selectedDeliveries.length) {
      setOpenEditDrawer(false);
      return;
    }

    setLoadLineItem((prev) => {
      const nextLoadNumber = (prev || []).length + 1;
      const nextLoad = {
        id: `loading-${Date.now()}`,
        load_number: nextLoadNumber,
        title: `Loading ${String(nextLoadNumber).padStart(2, '0')}`,
        deliveries: selectedDeliveries,
      };
      setExpandedLoadId(nextLoad.id);
      return updateLoadSequence([...(prev || []), nextLoad]);
    });

    setSelectedDeliveryMap({});
    setOpenEditDrawer(false);
  };

  const handleDeleteLoad = (loadObj) => {
    setLoadLineItem((prev) =>
      updateLoadSequence((prev || []).filter((item) => item.id !== loadObj.id)),
    );
    setExpandedLoadId((currentLoadId) =>
      currentLoadId === loadObj.id ? '' : currentLoadId,
    );
  };

  const renderLoadDeliveryCard = (deliveryObj, isReadOnly = false) => {
    const deliveryKey = deliveryObj?.delivery_unique_number;
    const lineItem = getFirstLineItem(deliveryObj);
    const product = lineItem?.product || {};

    return (
      <div className="create-load-delivery-card" key={deliveryKey}>
        <div className="div-flex-row div-space-between div-align-center">
          <span className="create-load-delivery-title text-bold">
            {deliveryObj?.sap_code || deliveryObj?.delivery_code || '-'}
          </span>
          {!isReadOnly && (
            <input
              aria-label={`Select ${deliveryObj?.delivery_code || deliveryKey}`}
              type="checkbox"
              checked={isDeliveryChecked(deliveryObj)}
              onChange={(e) =>
                handleToggleDeliverySelection(deliveryObj, e.target.checked)
              }
            />
          )}
        </div>

        <div className="create-load-delivery-divider" />

        <div className="duplicate-vehicle-tracking-load-field-grid">
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Delivery Date</span>
            <span className="text-sm text-bold">
              {deliveryObj?.delivery_date ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Plant</span>
            <span className="text-sm text-bold">
              {lineItem?.plant?.name ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Product Description</span>
            <span className="text-sm text-bold">
              {lineItem?.product_description ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Product Name</span>
            <span className="text-sm text-bold">
              {product?.product_name ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Product SAP Code</span>
            <span className="text-sm text-bold">
              {product?.product_sap_code ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">SAP Code</span>
            <span className="text-sm text-bold">
              {deliveryObj?.sap_code ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Ship To Party</span>
            <span className="text-sm text-bold">
              {deliveryObj?.ship_to_party?.name ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Total Quantity</span>
            <span className="text-sm text-bold">
              {deliveryObj?.total_quantity ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Weight</span>
            <span className="text-sm text-bold">
              {deliveryObj?.weight ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Pack Quantity</span>
            <span className="text-sm text-bold">
              {deliveryObj?.pack_quantity ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Pack Type</span>
            <span className="text-sm text-bold">
              {deliveryObj?.pack_type ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Sales Order Number</span>
            <span className="text-sm text-bold">
              {deliveryObj?.sales_order_number ?? '-'}
            </span>
          </div>
          <div className="duplicate-load-field div-flex-column rg-5">
            <span className="text-grey text-sm">Grade</span>
            <span className="text-sm text-bold">
              {deliveryObj?.grade ?? '-'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  function handlePreviousStep() {
    setSelectedStep(selectedStep - 1);
  }

  function handleNextStep() {
    setSelectedStep(selectedStep + 1);
  }

  return (
    <>
      <div className="duplicate-load-tab vertical-padding-10 horizontal-padding-10">
        <div className="div-flex-row div-space-between section-title div-align-center">
          <div className="text-xxl text-bold">Load List</div>

          <button
            type="button"
            className={`common-btn-outlined theme-border-color-${themeColor}`}
            onClick={toggleEditDrawer(true)}
          >
            Add Load
          </button>
        </div>

        <div
          className={`div-flex-column vertical-padding-10 horizontal-padding-10 rg-10 theme-vertical-scrollable-container-${themeColor}  duplicate-vehicle-tracking-load-list-container`}
        >
          {(loadLineItem || []).length > 0 ? (
            loadLineItem.map((loadObj) => {
              const isExpanded = expandedLoadId === loadObj.id;

              return (
                <div
                  className={`duplicate-vehicle-tracking-load-row ${isExpanded ? 'is-open' : ''}`}
                  key={loadObj.id}
                >
                  <button
                    type="button"
                    className="duplicate-vehicle-tracking-load-row-header"
                    onClick={() => handleToggleLoad(loadObj.id)}
                  >
                    <div className="div-flex-row div-align-center cg-10">
                      <GRNList className="duplicate-vehicle-tracking-load-icon" />
                      <span className="text-sm text-bold">{loadObj.title}</span>
                    </div>

                    <div className="div-flex-row div-align-center cg-10">
                      <Delete
                        className=" icon-size-md cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLoad(loadObj);
                        }}
                      />
                      <ArrowDown
                        className={`icon-size-sm ${
                          isExpanded ? 'rotate-icon-180' : ''
                        }`}
                        color={getColorCode(themeColor)}
                      />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="duplicate-vehicle-tracking-load-details div-flex-column rg-10">
                      {(loadObj.deliveries || []).map((deliveryObj) =>
                        renderLoadDeliveryCard(deliveryObj, true),
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-data-found-message border-bottom">
              <span className="text-sm">No Load Items Found</span>
            </div>
          )}
        </div>

        <Drawer
          anchor="right"
          open={Boolean(openEditDrawer)}
          onClose={toggleEditDrawer(false)}
          ModalProps={{ keepMounted: false }}
          sx={{ zIndex: 1 }}
          PaperProps={{
            sx: { width: '55%', minWidth: '720px' },
          }}
        >
          <div className="page-layout div-flex-column rg-10 right-form-layout">
            <div className="create-load-header div-flex-row div-align-center cg-10 horizontal-padding-10">
              <span className="text-xxl text-bold">Create Load</span>
            </div>

            <div className="vehicle-planning-status-pill-container div-flex-row div-align-center cg-10 border-bottom padding-bottom-10 horizontal-padding-10">
              <button
                type="button"
                className={`vehicle-planning-status-pill text-sm ${
                  activeDeliveryListTab === 'searched'
                    ? 'vehicle-planning-status-pill-active'
                    : ''
                }`}
                style={
                  activeDeliveryListTab === 'searched'
                    ? listTabActiveStyle
                    : undefined
                }
                onClick={() => setActiveDeliveryListTab('searched')}
              >
                Searched List
              </button>
              <button
                type="button"
                className={`vehicle-planning-status-pill text-sm ${
                  activeDeliveryListTab === 'attached'
                    ? 'vehicle-planning-status-pill-active'
                    : ''
                }`}
                style={
                  activeDeliveryListTab === 'attached'
                    ? listTabActiveStyle
                    : undefined
                }
                onClick={() => setActiveDeliveryListTab('attached')}
              >
                Attached List
              </button>
            </div>

            {activeDeliveryListTab === 'searched' && (
              <div className="div-flex-row cg-10 div-align-center horizontal-padding-10">
                <Row className="w-100">
                  <Col xxl={4} xl={4} lg={4} md={12} sm={12} xs={12}>
                    <label
                      className="form-label-section text-sm text-bold"
                      htmlFor="load-delivery-number"
                    >
                      Delivery Number
                    </label>
                    <div className="input-wrapper">
                      <input
                        id="load-delivery-number"
                        className="form-input-section margin-top-5"
                        type="text"
                        name="load-delivery-number"
                        placeholder="Search Delivery Number"
                        value={searchFormData.deliveryNumber}
                        onChange={(e) =>
                          handleSearchFormChange(
                            'deliveryNumber',
                            e.target.value,
                          )
                        }
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </Col>
                  <Col xxl={4} xl={4} lg={4} md={12} sm={12} xs={12}>
                    <label
                      className="form-label-section text-sm text-bold"
                      htmlFor="load-delivery-product"
                    >
                      Product
                    </label>
                    <div className="input-wrapper">
                      <input
                        id="load-delivery-product"
                        className="form-input-section margin-top-5"
                        type="text"
                        name="load-delivery-product"
                        placeholder="Search Delivery Product"
                        value={searchFormData.product}
                        onChange={(e) =>
                          handleSearchFormChange('product', e.target.value)
                        }
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </Col>
                  <Col
                    xxl={3}
                    xl={3}
                    lg={3}
                    md={12}
                    sm={12}
                    xs={12}
                    className="d-flex flex-column justify-content-end"
                  >
                    <button
                      type="button"
                      className={`search-purchase-order-btn theme-background-${themeColor}`}
                      onClick={handleSearchSubmit}
                    >
                      Search
                    </button>
                  </Col>
                </Row>
              </div>
            )}
            <div
              className={`create-load-card-list div-flex-column rg-20 ${
                activeDeliveryListTab === 'searched'
                  ? 'create-load-card-list-search-list'
                  : 'create-load-card-list-attached-list'
              } horizontal-padding-10 theme-scrollable-container-${themeColor}`}
            >
              {currentDeliveryList.length > 0 ? (
                currentDeliveryList.map((deliveryObj) =>
                  renderLoadDeliveryCard(deliveryObj),
                )
              ) : (
                <div className="no-data-found-message border-bottom">
                  <span className="text-sm">
                    {activeDeliveryListTab === 'searched'
                      ? 'No Delivery Items Found'
                      : 'No Delivery Attached Yet'}
                  </span>
                </div>
              )}
            </div>

            <div className="div-flex-row div-align-center div-space-between border-top padding-top-10 horizontal-padding-10">
              <button
                type="button"
                className={`common-btn-outlined theme-border-color-${themeColor}`}
                onClick={toggleEditDrawer(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`common-btn theme-background-${themeColor}`}
                onClick={handleCreateLoadSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </Drawer>
      </div>

      <div className=" div-flex-row div-space-between div-align-center vertical-padding-10 horizontal-padding-10 border-top">
        <button
          type="button"
          className={`common-btn-outlined theme-border-color-${themeColor}`}
          onClick={handlePreviousStep}
          disabled={selectedStep === 1}
        >
          Back
        </button>
        <button
          type="button"
          className={`common-btn theme-background-${themeColor}`}
          onClick={handleNextStep}
          disabled={selectedStep === totalSteps}
        >
          Next
        </button>
      </div>
    </>
  );
}

export default LoadTab;
